(() => {
  const slugify = (value) =>
    value
      .toLowerCase()
      .normalize('NFKD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-|-$/g, '') || 'section';

  const topLevelChild = (node, root) => {
    let current = node;
    while (current.parentElement && current.parentElement !== root) current = current.parentElement;
    return current;
  };

  const mediaWrapperFor = (image, article) => {
    const figure = image.closest('figure');
    if (figure && article.contains(figure)) return figure;

    const parent = image.parentElement;
    if (!parent || parent === article) return image;
    if (['P', 'DIV'].includes(parent.tagName) && parent.querySelectorAll('img').length === 1) return parent;
    return image;
  };

  const classifyImage = (image, article) => {
    const applyClass = () => {
      const width = Number(image.getAttribute('width')) || image.naturalWidth;
      const height = Number(image.getAttribute('height')) || image.naturalHeight;
      if (!width || !height) return;

      const ratio = width / height;
      const orientation = ratio < 0.82 ? 'portrait' : ratio > 1.18 ? 'landscape' : 'square';
      const wrapper = mediaWrapperFor(image, article);

      image.classList.add('tes-image', `tes-image--${orientation}`);
      wrapper.classList.add('tes-media', `tes-media--${orientation}`);
      wrapper.dataset.tesMedia = orientation;
    };

    if (!image.hasAttribute('loading')) image.loading = 'lazy';
    if (!image.hasAttribute('decoding')) image.decoding = 'async';

    if ((image.getAttribute('width') && image.getAttribute('height')) || image.complete) applyClass();
    else image.addEventListener('load', applyClass, { once: true });
  };

  const collectLeadCopy = (article, mediaNode) => {
    const children = [...article.children];
    const mediaIndex = children.indexOf(mediaNode);
    if (mediaIndex < 0) return [];

    const before = [];
    for (let index = mediaIndex - 1; index >= 0 && before.length < 2; index -= 1) {
      const node = children[index];
      if (/^H[1-6]$/.test(node.tagName)) break;
      if (node.tagName === 'P' && node.textContent.trim()) before.unshift(node);
      else if (node.textContent.trim()) break;
    }
    if (before.length) return before;

    const after = [];
    for (let index = mediaIndex + 1; index < children.length && after.length < 2; index += 1) {
      const node = children[index];
      if (/^H[1-6]$/.test(node.tagName)) break;
      if (node.tagName === 'P' && node.textContent.trim()) after.push(node);
      else if (node.textContent.trim()) break;
    }
    return after;
  };

  const buildLeadFeature = (article, layoutType) => {
    if (!layoutType || layoutType === 'none' || article.querySelector('.tes-lead-feature')) return;

    const firstImage = article.querySelector('img.tes-image');
    if (!firstImage) return;

    const media = mediaWrapperFor(firstImage, article);
    const mediaNode = topLevelChild(media, article);
    const copyNodes = collectLeadCopy(article, mediaNode);
    if (!copyNodes.length) return;

    const feature = document.createElement('section');
    feature.className = `tes-lead-feature tes-lead-feature--${layoutType}`;
    feature.setAttribute('aria-label', 'Article introduction');

    const copy = document.createElement('div');
    copy.className = 'tes-lead-feature__copy';
    const mediaColumn = document.createElement('div');
    mediaColumn.className = `tes-lead-feature__media tes-lead-feature__media--${firstImage.classList.contains('tes-image--portrait') ? 'portrait' : 'wide'}`;

    const insertBefore = copyNodes[0].compareDocumentPosition(mediaNode) & Node.DOCUMENT_POSITION_FOLLOWING
      ? copyNodes[0]
      : mediaNode;
    article.insertBefore(feature, insertBefore);

    copyNodes.forEach((node) => copy.appendChild(node));
    mediaColumn.appendChild(mediaNode);
    feature.append(copy, mediaColumn);
  };

  const buildToc = (layout, article) => {
    const mode = layout.dataset.tesTocMode || 'cards';
    if (mode === 'none') return;

    const toc = layout.querySelector('[data-tes-toc]');
    const list = layout.querySelector('[data-tes-toc-list]');
    const count = layout.querySelector('[data-tes-toc-count]');
    const progress = layout.querySelector('[data-tes-toc-progress]');
    const headings = [...article.querySelectorAll('h2')].filter((heading) => heading.textContent.trim());
    const minimum = 2;
    if (!toc || !list || headings.length < minimum) return;

    const usedIds = new Set([...document.querySelectorAll('[id]')].map((element) => element.id));
    const links = [];

    headings.forEach((heading, index) => {
      if (!heading.id) {
        const base = slugify(heading.textContent.trim());
        let candidate = base;
        let suffix = 2;
        while (usedIds.has(candidate)) candidate = `${base}-${suffix++}`;
        heading.id = candidate;
        usedIds.add(candidate);
      }

      heading.style.setProperty('--tes-section-number', `'${String(index + 1)}'`);
      const item = document.createElement('li');
      const link = document.createElement('a');
      link.href = `#${heading.id}`;
      link.textContent = heading.textContent.trim();
      item.appendChild(link);
      list.appendChild(item);
      links.push({ heading, link });
    });

    if (count) count.textContent = `${headings.length} sections`;
    toc.hidden = false;

    let ticking = false;
    const updateActive = () => {
      ticking = false;
      const marker = 170;
      let activeIndex = 0;
      headings.forEach((heading, index) => {
        if (heading.getBoundingClientRect().top <= marker) activeIndex = index;
      });

      links.forEach(({ link }, index) => {
        if (index === activeIndex) link.setAttribute('aria-current', 'true');
        else link.removeAttribute('aria-current');
      });
      if (progress) progress.style.transform = `scaleX(${(activeIndex + 1) / headings.length})`;
    };

    const requestUpdate = () => {
      if (ticking) return;
      ticking = true;
      window.requestAnimationFrame(updateActive);
    };

    updateActive();
    window.addEventListener('scroll', requestUpdate, { passive: true });
    window.addEventListener('resize', requestUpdate, { passive: true });
  };

  const init = (root = document) => {
    root.querySelectorAll('[data-tes-reading-layout]').forEach((layout) => {
      if (layout.dataset.tesInitialized === 'true') return;
      const article = layout.querySelector('[data-tes-article]');
      if (!article) return;

      layout.dataset.tesInitialized = 'true';
      article.querySelectorAll('img').forEach((image) => classifyImage(image, article));
      buildLeadFeature(article, layout.dataset.tesLeadLayout);
      buildToc(layout, article);
    });
  };

  const setShareStatus = (button, message) => {
    const component = button.closest('.tes-article-share');
    const status = component?.querySelector('[data-tes-share-status]');
    if (status) status.textContent = message;
  };

  const localCopy = (url) => {
    const field = document.createElement('textarea');
    field.value = url;
    field.setAttribute('readonly', '');
    field.style.position = 'fixed';
    field.style.opacity = '0';
    document.body.appendChild(field);
    field.select();
    let copied = false;
    try {
      copied = document.execCommand('copy');
    } finally {
      field.remove();
    }
    if (!copied) throw new Error('Copy command failed');
  };

  const copyShareUrl = async (url) => {
    if (navigator.clipboard?.writeText) {
      try {
        await navigator.clipboard.writeText(url);
        return;
      } catch (_) {
        // Continue to the built-in local fallback.
      }
    }
    localCopy(url);
  };

  const initShareControls = (root = document) => {
    root.querySelectorAll('[data-tes-native-share]').forEach((button) => {
      if (button.dataset.tesShareInitialized === 'true') return;
      button.dataset.tesShareInitialized = 'true';
      button.addEventListener('click', async () => {
        const title = button.dataset.shareTitle || '';
        const url = button.dataset.shareUrl || '';
        setShareStatus(button, '');

        if (navigator.share) {
          try {
            await navigator.share({ title, url });
            return;
          } catch (error) {
            if (error?.name === 'AbortError') return;
          }
        }

        try {
          await copyShareUrl(url);
          setShareStatus(button, 'Link copied');
        } catch (_) {
          setShareStatus(button, 'Unable to share or copy link');
        }
      });
    });
  };

  const normalizePreviewLinks = () => {
    const previewViews = new Set(['tes-honey-guide', 'tes-wellness-library']);
    const honeyPaths = new Set([
      '/blogs/honey-basics-care/local-honey-las-vegas',
      '/blogs/honey-basics-care/local-honey-for-allergies',
      '/blogs/honey-basics-care/fermented-honey-origin-story',
    ]);
    const currentUrl = new URL(window.location.href);
    if (!previewViews.has(currentUrl.searchParams.get('view'))) return;

    document.querySelectorAll('a[href]').forEach((link) => {
      const url = new URL(link.href, window.location.href);
      if (url.origin !== window.location.origin || !honeyPaths.has(url.pathname)) return;
      url.searchParams.set('view', 'tes-honey-guide');
      link.href = url.href;
    });

    const breadcrumbLibraryLink = [...document.querySelectorAll('.tes-breadcrumbs a')].find(
      (link) => link.textContent.trim() === 'Wellness Library',
    );
    const libraryTarget =
      breadcrumbLibraryLink?.href ||
      (currentUrl.searchParams.get('view') === 'tes-wellness-library' ? currentUrl.href : '');
    if (!libraryTarget) return;

    document.querySelectorAll('header a[href]').forEach((link) => {
      if (link.textContent.trim() === 'Blog') link.href = libraryTarget;
    });
  };

  init();
  initShareControls();
  normalizePreviewLinks();
  document.addEventListener('shopify:section:load', (event) => {
    init(event.target);
    initShareControls(event.target);
    normalizePreviewLinks();
  });
})();
